import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import PortalLayout from '@/components/portal/PortalLayout';
import { Loader2, Plus, Edit, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { FieldDictionary } from '@/types/fieldsDictionary';

const typeOptions = [
  { value: 'string', label: 'String' },
  { value: 'int', label: 'Number' },
  { value: 'bool', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'timestamptz', label: 'Datetime' },
];

const FieldsDictionaryPage: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<FieldDictionary | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    key: '',
    friendly_name: '',
    description: '',
    context: '',
    type: 'string',
  });

  const { data: fields, isLoading, error, isError } = useQuery<FieldDictionary[], Error>({
    queryKey: ['fieldsDictionary'],
    queryFn: async () => {
      const { getFieldsDictionary } = await import('@/api/admin');
      return await getFieldsDictionary();
    },
  });

  const createFieldMutation = useMutation({
    mutationFn: async (fieldData: FieldDictionary) => {
      const { createFieldDictionary } = await import('@/api/admin');
      return await createFieldDictionary({
        key: fieldData.key,
        friendly_name: fieldData.friendly_name,
        description: fieldData.description || null,
        context: fieldData.context || null,
        type: fieldData.type,
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Field created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['fieldsDictionary'] });
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create field.",
        variant: "destructive",
      });
    },
  });

  const updateFieldMutation = useMutation({
    mutationFn: async ({ id, fieldData }: { id: string; fieldData: FieldDictionary }) => {
      const { updateFieldDictionary } = await import('@/api/admin');
      return await updateFieldDictionary(id, {
        key: fieldData.key,
        friendly_name: fieldData.friendly_name,
        description: fieldData.description || null,
        context: fieldData.context || null,
        type: fieldData.type,
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Field updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['fieldsDictionary'] });
      setIsCreateDialogOpen(false);
      setEditingField(null);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update field.",
        variant: "destructive",
      });
    },
  });

  const deleteFieldMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      const { deleteFieldDictionary } = await import('@/api/admin');
      return await deleteFieldDictionary(fieldId);
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Field deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['fieldsDictionary'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete field.",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      key: '',
      friendly_name: '',
      description: '',
      context: '',
      type: 'string',
    });
  };

  const handleEdit = (field: FieldDictionary) => {
    setEditingField(field);
    setFormData({
      key: field.key,
      friendly_name: field.friendly_name,
      description: field.description || '',
      context: field.context || '',
      type: field.type,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.type) {
      toast({
        title: "Validation Error",
        description: "Type field is required.",
        variant: "destructive",
      });
      return;
    }
    
    if (editingField) {
      updateFieldMutation.mutate({ id: editingField.id, fieldData: formData });
    } else {
      createFieldMutation.mutate(formData);
    }
  };

  const handleDelete = (fieldId: string) => {
    if (confirm('Are you sure you want to delete this field? This action cannot be undone.')) {
      deleteFieldMutation.mutate(fieldId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load fields dictionary: {error?.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Keys Dictionary</h1>
            <p className="text-gray-600 mt-2">
              Manage field and their definitions.
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setEditingField(null); }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Key
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingField ? 'Edit Field' : 'Create New Field'}</DialogTitle>
                <DialogDescription>
                  {editingField ? 'Update the field details below.' : 'Fill in the details for the new field definition.'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="key">Key</Label>
                  <Input
                    id="key"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    required
                    placeholder="e.g., user_email"
                  />
                </div>
                <div>
                  <Label htmlFor="friendly_name">Friendly Name</Label>
                  <Input
                    id="friendly_name"
                    value={formData.friendly_name}
                    onChange={(e) => setFormData({ ...formData, friendly_name: e.target.value })}
                    required
                    placeholder="e.g., User Email"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter a detailed description of the field"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="context">Context</Label>
                  <Textarea
                    id="context"
                    value={formData.context}
                    onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                    placeholder="Enter context information for AI"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="type">Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                    required
                  >
                    <SelectTrigger className={!formData.type ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                    <SelectContent>
                      {typeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => {
                    setIsCreateDialogOpen(false);
                    setEditingField(null);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createFieldMutation.isPending || updateFieldMutation.isPending}>
                    {(createFieldMutation.isPending || updateFieldMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {editingField ? 'Update Field' : 'Create Field'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {!isLoading && !isError && fields && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Friendly Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Context</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    No fields found.
                  </TableCell>
                </TableRow>
              ) : (
                fields.map((field: FieldDictionary) => (
                  <TableRow key={field.id}>
                    <TableCell className="font-medium font-mono">{field.key}</TableCell>
                    <TableCell>{field.friendly_name}</TableCell>
                    <TableCell>{field.description || 'N/A'}</TableCell>
                    <TableCell>{field.context || 'N/A'}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                        {typeOptions.find(opt => opt.value === field.type)?.label || field.type}
                      </span>
                    </TableCell>
                    <TableCell>
                        {field.key !== 'first_name'
                            && field.key !== 'last_name'
                            && field.key !== 'phone'
                            && field.key !== 'email'
                            && (
                                <div className="flex space-x-2">
                                  <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        handleEdit(field);
                                        setIsCreateDialogOpen(true);
                                      }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDelete(field.id)}
                                      disabled={deleteFieldMutation.isPending}
                                  >
                                    {deleteFieldMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Trash2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                                )
                        }
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

      </div>
    </PortalLayout>
  );
};

export default FieldsDictionaryPage;

